import React, { useEffect, useRef, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Database } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};
const exactTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('en-IN');
};

/** Top-right control: shows a "CRM data as of <time>" freshness indicator to ALL
 *  roles, plus a "Sync from CRM" button for admin/manager (endpoint is gated). */
export default function SyncFromCrmButton({ onDone }) {
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const poll = useRef(null);

  const canSync = user?.role === 'admin' || user?.role === 'manager';
  const syncedAt = lastSync?.completed_at || lastSync?.started_at || '';

  const fetchStatus = async () => {
    try {
      const r = await api.get('/training/zoho/crm-sync-status');
      setLastSync(r.data?.last_sync || null);
      return !!r.data?.is_running;
    } catch { return false; }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (poll.current) clearInterval(poll.current); };
  }, []); // eslint-disable-line

  const startPolling = () => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const isRunning = await fetchStatus();
      if (!isRunning) {
        clearInterval(poll.current); poll.current = null;
        setRunning(false);
        toast.success('CRM sync complete — latest batch details loaded');
        onDone?.();
      }
    }, 4000);
  };

  const sync = async () => {
    setRunning(true);
    try {
      const r = await api.post('/training/zoho/crm-sync', {});
      toast.message(r.data?.status === 'already_running'
        ? 'CRM sync already in progress…'
        : 'CRM sync started — fetching latest from Zoho…');
      startPolling();
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || 'Failed to start CRM sync');
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-500"
        title={syncedAt ? `Last Zoho CRM batch sync: ${exactTime(syncedAt)}` : 'No CRM sync recorded yet'}
        data-testid="crm-data-asof"
      >
        <Database className="w-3.5 h-3.5 text-slate-400" />
        {running ? 'Syncing from CRM…' : syncedAt ? <>CRM data as of <span className="text-slate-700 font-semibold">{timeAgo(syncedAt)}</span></> : 'CRM not synced yet'}
      </span>
      {canSync && (
        <Button size="sm" variant="outline" onClick={sync} disabled={running}
          className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="sync-crm-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Syncing…' : 'Sync from CRM'}
        </Button>
      )}
    </div>
  );
}
